'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { toast, Toaster } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import Breadcrumb from '@/components/Breadcrumb';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

interface Scadenza {
  id: string;
  tipo: 'garanzia' | 'contratto_manutenzione' | 'licenza' | 'comodato';
  titolo: string;
  device_id: string | null;
  fornitore_id: string | null;
  data_inizio: string | null;
  data_scadenza: string;
  importo: number;
  numero_contratto: string;
  note: string;
  notifica_giorni: number;
  device?: { hostname: string } | null;
  fornitore?: { ragione_sociale: string } | null;
}

interface Device { id: string; hostname: string; }
interface Fornitore { id: string; ragione_sociale: string; }

const TIPO_LABELS: Record<string, string> = {
  garanzia: 'Garanzia',
  contratto_manutenzione: 'Contratto Manutenzione',
  licenza: 'Licenza Software',
  comodato: 'Comodato',
};

const TIPO_COLORS: Record<string, string> = {
  garanzia: 'bg-emerald-500/20 text-emerald-400',
  contratto_manutenzione: 'bg-sky-500/20 text-sky-400',
  licenza: 'bg-purple-500/20 text-purple-400',
  comodato: 'bg-amber-500/20 text-amber-400',
};

const MONTHS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

function getDaysUntil(dateStr: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr);
  return Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

function getUrgencyColor(days: number): string {
  if (days < 0) return 'text-red-400 bg-red-500/20';
  if (days <= 7) return 'text-red-400 bg-red-500/20';
  if (days <= 30) return 'text-amber-400 bg-amber-500/20';
  if (days <= 60) return 'text-yellow-400 bg-yellow-500/20';
  return 'text-emerald-400 bg-emerald-500/20';
}

type ScadenzaTipo = 'garanzia' | 'contratto_manutenzione' | 'licenza' | 'comodato';

const emptyScadenza: {
  tipo: ScadenzaTipo; titolo: string; device_id: string | null; fornitore_id: string | null;
  data_inizio: string; data_scadenza: string; importo: number;
  numero_contratto: string; note: string; notifica_giorni: number;
} = {
  tipo: 'garanzia',
  titolo: '', device_id: null, fornitore_id: null,
  data_inizio: '', data_scadenza: '', importo: 0,
  numero_contratto: '', note: '', notifica_giorni: 30,
};

export default function ScadenzeContent() {
  const [scadenze, setScadenze] = useState<Scadenza[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [fornitori, setFornitori] = useState<Fornitore[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [filterTipo, setFilterTipo] = useState<string>('all');
  const [filterUrgenza, setFilterUrgenza] = useState<string>('all');
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState<Scadenza | null>(null);
  const [formData, setFormData] = useState<typeof emptyScadenza>({ ...emptyScadenza });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; id: string; name: string }>({ open: false, id: '', name: '' });
  const { canWrite } = useAuth();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, dRes, fRes] = await Promise.all([
        supabase.from('scadenze').select('*, device:devices(hostname), fornitore:fornitori(ragione_sociale)').order('data_scadenza'),
        supabase.from('devices').select('id, hostname').order('hostname'),
        supabase.from('fornitori').select('id, ragione_sociale').order('ragione_sociale'),
      ]);
      if (sRes.error) throw sRes.error;
      setScadenze(sRes.data || []);
      setDevices(dRes.data || []);
      setFornitori(fRes.data || []);
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = scadenze.filter(s => {
    if (filterTipo !== 'all' && s.tipo !== filterTipo) return false;
    if (filterUrgenza !== 'all') {
      const days = getDaysUntil(s.data_scadenza);
      if (filterUrgenza === 'scadute' && days >= 0) return false;
      if (filterUrgenza === 'urgenti' && (days < 0 || days > 7)) return false;
      if (filterUrgenza === 'prossime' && (days < 0 || days > 30)) return false;
    }
    return true;
  });

  const calendarScadenze = scadenze.filter(s => {
    const d = new Date(s.data_scadenza);
    return d.getFullYear() === calYear && d.getMonth() === calMonth;
  });

  const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay();

  const openCreate = () => {
    setEditItem(null);
    setFormData({ ...emptyScadenza });
    setShowForm(true);
  };

  const openEdit = (s: Scadenza) => {
    setEditItem(s);
    setFormData({
      tipo: s.tipo, titolo: s.titolo, device_id: s.device_id, fornitore_id: s.fornitore_id,
      data_inizio: s.data_inizio || '', data_scadenza: s.data_scadenza,
      importo: s.importo, numero_contratto: s.numero_contratto, note: s.note, notifica_giorni: s.notifica_giorni,
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.titolo || !formData.data_scadenza) { toast.error('Titolo e data scadenza obbligatori'); return; }
    if (formData.importo < 0) { toast.error('Importo non può essere negativo'); return; }
    if (formData.data_inizio && formData.data_scadenza && new Date(formData.data_scadenza) <= new Date(formData.data_inizio)) {
      toast.error('La data di scadenza deve essere successiva alla data di inizio');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...formData, data_inizio: formData.data_inizio || null, device_id: formData.device_id || null, fornitore_id: formData.fornitore_id || null };
      if (editItem) {
        const { error } = await supabase.from('scadenze').update({ ...payload, updated_at: new Date().toISOString() }).eq('id', editItem.id);
        if (error) throw error;
        toast.success('Scadenza aggiornata');
      } else {
        const { error } = await supabase.from('scadenze').insert(payload);
        if (error) throw error;
        toast.success('Scadenza creata');
      }
      setShowForm(false);
      fetchData();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      const { error } = await supabase.from('scadenze').delete().eq('id', deleteConfirm.id);
      if (error) throw error;
      toast.success('Scadenza eliminata');
      setDeleteConfirm({ open: false, id: '', name: '' });
      fetchData();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    }
  };

  const stats = {
    scadute: scadenze.filter(s => getDaysUntil(s.data_scadenza) < 0).length,
    urgenti: scadenze.filter(s => { const d = getDaysUntil(s.data_scadenza); return d >= 0 && d <= 7; }).length,
    prossime: scadenze.filter(s => { const d = getDaysUntil(s.data_scadenza); return d > 7 && d <= 30; }).length,
    totali: scadenze.length,
  };

  return (
    <div className="flex flex-col h-full bg-background">
      <Toaster richColors position="top-right" />
      <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-surface">
        <div>
          <Breadcrumb items={[{ label: 'Calendario Scadenze' }]} />
          <h1 className="text-2xl font-bold text-white">Calendario Scadenze</h1>
          <p className="text-sm text-muted-color mt-0.5">Garanzie, contratti di manutenzione, licenze e comodati</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1 bg-surface-elevated rounded-lg p-1">
            {(['list', 'calendar'] as const).map(m => (
              <button key={m} onClick={() => setViewMode(m)}
                className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${viewMode === m ? 'bg-sky-500 text-white' : 'text-muted-color hover:text-white'}`}>
                {m === 'list' ? 'Lista' : 'Calendario'}
              </button>
            ))}
          </div>
          {canWrite() && (
            <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-sky-500 hover:bg-sky-600 text-white rounded-lg font-semibold transition-colors">
              <Icon name="PlusIcon" size={18} /> Nuova Scadenza
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 px-6 py-4 border-b border-border">
        {[
          { label: 'Scadute', value: stats.scadute, color: 'text-red-400', bg: 'bg-red-500/10' },
          { label: 'Urgenti (7gg)', value: stats.urgenti, color: 'text-amber-400', bg: 'bg-amber-500/10' },
          { label: 'Prossime (30gg)', value: stats.prossime, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
          { label: 'Totali', value: stats.totali, color: 'text-sky-400', bg: 'bg-sky-500/10' },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-xl p-4 flex items-center gap-3`}>
            <span className={`text-3xl font-bold ${s.color}`}>{s.value}</span>
            <span className="text-sm text-secondary-color">{s.label}</span>
          </div>
        ))}
      </div>

      <div className="flex-1 overflow-auto p-6">
        {viewMode === 'list' ? (
          <>
            {/* Filters */}
            <div className="flex gap-3 mb-4">
              <select value={filterTipo} onChange={e => setFilterTipo(e.target.value)}
                className="bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-500">
                <option value="all">Tutti i tipi</option>
                {Object.entries(TIPO_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <select value={filterUrgenza} onChange={e => setFilterUrgenza(e.target.value)}
                className="bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-500">
                <option value="all">Tutte le urgenze</option>
                <option value="scadute">Scadute</option>
                <option value="urgenti">Urgenti (7gg)</option>
                <option value="prossime">Prossime (30gg)</option>
              </select>
            </div>

            {loading ? (
              <div className="flex items-center justify-center h-48">
                <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="space-y-3">
                {filtered.map(s => {
                  const days = getDaysUntil(s.data_scadenza);
                  return (
                    <div key={s.id} className="bg-surface border border-border rounded-xl p-5 hover:border-sky-500/40 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="flex flex-col items-center gap-1">
                            <span className={`text-xs font-bold px-2 py-1 rounded-full ${TIPO_COLORS[s.tipo]}`}>{TIPO_LABELS[s.tipo]}</span>
                          </div>
                          <div>
                            <h3 className="font-bold text-white">{s.titolo}</h3>
                            <div className="flex flex-wrap gap-3 mt-1 text-sm text-muted-color">
                              {s.device?.hostname && <span>Dispositivo: <span className="text-secondary-color">{s.device.hostname}</span></span>}
                              {s.fornitore?.ragione_sociale && <span>Fornitore: <span className="text-secondary-color">{s.fornitore.ragione_sociale}</span></span>}
                              {s.numero_contratto && <span>N° {s.numero_contratto}</span>}
                              {s.importo > 0 && <span>€ {s.importo.toLocaleString('it-IT')}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="text-sm text-muted-color">Scadenza</p>
                            <p className="font-bold text-white">{new Date(s.data_scadenza).toLocaleDateString('it-IT')}</p>
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${getUrgencyColor(days)}`}>
                              {days < 0 ? `Scaduta ${Math.abs(days)}gg fa` : days === 0 ? 'Scade oggi' : `${days} giorni`}
                            </span>
                          </div>
                          {canWrite() && (
                            <div className="flex gap-1">
                              <button onClick={() => openEdit(s)} className="p-2 rounded-lg hover:bg-sky-500/10 text-sky-400 transition-colors"><Icon name="PencilIcon" size={14} /></button>
                              <button onClick={() => setDeleteConfirm({ open: true, id: s.id, name: s.titolo })} className="p-2 rounded-lg hover:bg-red-500/10 text-red-400 transition-colors"><Icon name="TrashIcon" size={14} /></button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {filtered.length === 0 && (
                  <div className="flex flex-col items-center justify-center h-48 text-muted-color">
                    <Icon name="CalendarIcon" size={48} className="mb-3 opacity-30" />
                    <p className="text-lg font-semibold">Nessuna scadenza trovata</p>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          /* Calendar View */
          <div>
            <div className="flex items-center justify-between mb-4">
              <button onClick={() => { if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); } else setCalMonth(m => m - 1); }}
                className="p-2 rounded-lg hover:bg-surface-elevated text-muted-color transition-colors">
                <Icon name="ChevronLeftIcon" size={20} />
              </button>
              <h2 className="text-xl font-bold text-white">{MONTHS[calMonth]} {calYear}</h2>
              <button onClick={() => { if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); } else setCalMonth(m => m + 1); }}
                className="p-2 rounded-lg hover:bg-surface-elevated text-muted-color transition-colors">
                <Icon name="ChevronRightIcon" size={20} />
              </button>
            </div>
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'].map(d => (
                <div key={d} className="text-center text-xs font-bold text-muted-color py-2">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: getFirstDayOfMonth(calYear, calMonth) }).map((_, i) => (
                <div key={`empty-${i}`} className="h-24 rounded-lg" />
              ))}
              {Array.from({ length: getDaysInMonth(calYear, calMonth) }).map((_, i) => {
                const day = i + 1;
                const dayScadenze = calendarScadenze.filter(s => new Date(s.data_scadenza).getDate() === day);
                const isToday = new Date().getDate() === day && new Date().getMonth() === calMonth && new Date().getFullYear() === calYear;
                return (
                  <div key={day} className={`h-24 rounded-lg border p-1.5 overflow-hidden ${isToday ? 'border-sky-500 bg-sky-500/5' : 'border-border bg-surface'}`}>
                    <span className={`text-xs font-bold ${isToday ? 'text-sky-400' : 'text-secondary-color'}`}>{day}</span>
                    <div className="mt-1 space-y-0.5">
                      {dayScadenze.slice(0, 2).map(s => (
                        <div key={s.id} className={`text-xs px-1 py-0.5 rounded truncate font-semibold ${TIPO_COLORS[s.tipo]}`}>
                          {s.titolo}
                        </div>
                      ))}
                      {dayScadenze.length > 2 && <div className="text-xs text-muted-color">+{dayScadenze.length - 2} altri</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2 className="text-xl font-bold text-white">{editItem ? 'Modifica' : 'Nuova'} Scadenza</h2>
              <button onClick={() => setShowForm(false)} className="p-2 rounded-lg hover:bg-surface-elevated text-muted-color"><Icon name="XMarkIcon" size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-1.5">Tipo *</label>
                <select value={formData.tipo} onChange={e => setFormData({ ...formData, tipo: e.target.value as any })}
                  className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-sky-500">
                  {Object.entries(TIPO_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-1.5">Titolo *</label>
                <input value={formData.titolo} onChange={e => setFormData({ ...formData, titolo: e.target.value })}
                  className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                  placeholder="es. Garanzia Analizzatore XN-1000" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-1.5">Dispositivo</label>
                <select value={formData.device_id || ''} onChange={e => setFormData({ ...formData, device_id: e.target.value || null })}
                  className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-sky-500">
                  <option value="">Nessun dispositivo</option>
                  {devices.map(d => <option key={d.id} value={d.id}>{d.hostname}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-1.5">Fornitore</label>
                <select value={formData.fornitore_id || ''} onChange={e => setFormData({ ...formData, fornitore_id: e.target.value || null })}
                  className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-sky-500">
                  <option value="">Nessun fornitore</option>
                  {fornitori.map(f => <option key={f.id} value={f.id}>{f.ragione_sociale}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Data Inizio</label>
                  <input type="date" value={formData.data_inizio} onChange={e => setFormData({ ...formData, data_inizio: e.target.value })}
                    className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-sky-500" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Data Scadenza *</label>
                  <input type="date" value={formData.data_scadenza} onChange={e => setFormData({ ...formData, data_scadenza: e.target.value })}
                    className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-sky-500" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Importo (€)</label>
                  <input type="number" min={0} step={0.01} value={formData.importo} onChange={e => setFormData({ ...formData, importo: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-sky-500" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Notifica (giorni prima)</label>
                  <input type="number" min={1} value={formData.notifica_giorni} onChange={e => setFormData({ ...formData, notifica_giorni: parseInt(e.target.value) || 30 })}
                    className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-sky-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-1.5">Numero Contratto</label>
                <input value={formData.numero_contratto} onChange={e => setFormData({ ...formData, numero_contratto: e.target.value })}
                  className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                  placeholder="es. MANT-2025-001" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-1.5">Note</label>
                <textarea value={formData.note} onChange={e => setFormData({ ...formData, note: e.target.value })} rows={2}
                  className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500 resize-none"
                  placeholder="Note aggiuntive..." />
              </div>
            </div>
            <div className="flex gap-3 p-6 border-t border-border">
              <button onClick={() => setShowForm(false)} className="flex-1 px-4 py-2.5 border border-border rounded-lg text-secondary-color hover:bg-surface-elevated font-semibold transition-colors">Annulla</button>
              <button onClick={handleSave} disabled={saving || !formData.titolo || !formData.data_scadenza}
                className="flex-1 px-4 py-2.5 bg-sky-500 hover:bg-sky-600 disabled:opacity-50 text-white rounded-lg font-semibold transition-colors">
                {saving ? 'Salvataggio...' : editItem ? 'Aggiorna' : 'Crea'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={deleteConfirm.open}
        title="Elimina Scadenza"
        message={`Sei sicuro di voler eliminare "${deleteConfirm.name}"?`}
        confirmLabel="Elimina"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm({ open: false, id: '', name: '' })}
      />
    </div>
  );
}
